import React from 'react';
import { ArrowLeft, CheckCircle, Star, Wrench, Shield, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { products, companyInfo } from '../mock';

export const ProductDetail = ({ productId, onBack }) => {
  const product = products.find(p => p.id === parseInt(productId));

  // Scroll to top when component loads
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Product Not Found</h2>
          <Button onClick={onBack} className="bg-[#022d47] hover:bg-[#033858]">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  const specifications = {
    1: { // Stainless Steel Pipes & Tubes
      standards: ['ASTM A312 (Seamless)', 'ASTM A358 (Welded)', 'ASTM A213 (Seamless)', 'ASTM A249 (Welded)', 'ASME SA312', 'EN 10216-5'],
      sizes: ['1/8" to 48"', 'SCH 5S to SCH XXS'],
      forms: ['Seamless', 'Welded', 'ERW', 'EFW']
    },
    2: { // Sheets & Plates
      standards: ['ASTM A240', 'ASTM A167', 'JIS G4304', 'EN 10088'],
      sizes: ['0.4mm to 100mm thickness', '1000mm to 2500mm width'],
      forms: ['Hot Rolled', 'Cold Rolled', 'Pickled'],
      finishes: ['2B', 'BA', 'No.1', 'No.4', 'HL', 'Mirror']
    },
    3: { // Round Bars
      standards: ['ASTM A276', 'ASTM A479', 'ASTM A564', 'JIS G4303'],
      sizes: ['3mm to 500mm diameter', 'Up to 12m length'],
      finishes: ['Black', 'Bright']
    },
    4: { // Flanges & Fittings
      standards: ['ASTM A182', 'ASME B16.5', 'MSS SP-97', 'EN 1092'],
      sizes: ['1/2" to 60"', 'Class 150 to 2500'],
      forms: ['Slip-On', 'Weld Neck', 'Blind', 'Socket Weld', 'Threaded']
    },
    5: { // Nickel Alloys
      standards: ['ASTM B564 (Round Bars)', 'ASTM B167 (Pipes)', 'ASTM B168 (Plates)', 'ASME SB-564', 'UNS N06625', 'UNS N07718'],
      forms: ['Round Bar', 'Sheets', 'Plates', 'Pipes']
    },
    6: { // Duplex & Super Duplex
      standards: ['ASTM A790 (Pipes)', 'ASTM A928 (Pipes)', 'ASTM A240 (Plates)', 'ASTM A276 (Round Bars)', 'EN 10216-5'],
      forms: ['Round Bar', 'Sheets', 'Plates', 'Pipes']
    },
    7: { // Special Alloys
      standards: ['ASTM B265 (Titanium)', 'ASTM B348 (Titanium Bars)', 'ASTM B337 (Titanium Pipes)', 'ASTM B381 (Titanium Forgings)', 'AMS 4928'],
      forms: ['Round Bar', 'Sheets', 'Plates', 'Pipes']
    },
    8: { // Forged Components
      standards: ['ASTM A182', 'ASTM A336', 'ASTM A694', 'ASME SA-182', 'EN 10222-5', 'MSS SP-44']
    },
    9: { // Alloy Steel Round Bars
      standards: ['ASTM A829', 'ASTM A322', 'EN 10083', 'JIS G4105', 'DIN 1.7225', 'AISI 4140'],
      sizes: ['6mm to 500mm diameter', 'Up to 12m length'],
      finishes: ['Black', 'Bright']
    },
    10: { // Finished Shafts
      standards: ['As per customer drawing', 'ISO 2768', 'DIN 6930', 'Customer specifications'],
      sizes: ['Custom dimensions', 'As per drawing'],
      forms: ['CNC Machined', 'Turned', 'Ground', 'Heat Treated']
    }
  };

  const currentSpecs = specifications[product.id] || specifications[1];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              onClick={onBack}
              className="hover:border-[#022d47] hover:text-[#022d47]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Products
            </Button>
            <Button 
              className="bg-[#022d47] hover:bg-[#033858]"
              onClick={() => window.location.href = `mailto:vedaant@jpsmetal.in?subject=Quote Request for ${product.name}`}
            >
              Request Quote
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 lg:py-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Product Image */}
          <div className="space-y-4 lg:space-y-6">
            <div className="aspect-w-16 aspect-h-12 overflow-hidden rounded-xl bg-white shadow-lg">
              <img 
                src={product.image} 
                alt={product.name}
                className="w-full h-64 lg:h-96 object-cover"
                loading="lazy"
              />
            </div>
            
            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-2 lg:gap-4">
              <div className="bg-white p-2 lg:p-4 rounded-lg shadow-sm text-center">
                <Shield className="h-4 w-4 lg:h-6 lg:w-6 text-green-600 mx-auto mb-2" />
                <div className="text-xs lg:text-sm font-medium text-slate-900">ISO Certified</div>
              </div>
              <div className="bg-white p-2 lg:p-4 rounded-lg shadow-sm text-center">
                <Star className="h-4 w-4 lg:h-6 lg:w-6 text-yellow-500 mx-auto mb-2" />
                <div className="text-xs lg:text-sm font-medium text-slate-900">Premium Quality</div>
              </div>
              <div className="bg-white p-2 lg:p-4 rounded-lg shadow-sm text-center">
                <Clock className="h-4 w-4 lg:h-6 lg:w-6 text-blue-600 mx-auto mb-2" />
                <div className="text-xs lg:text-sm font-medium text-slate-900">Quick Delivery</div>
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6 lg:space-y-8">
            <div>
              <h1 className="text-2xl lg:text-4xl font-bold text-slate-900 mb-4">
                {product.name}
              </h1>
              <p className="text-lg lg:text-xl text-slate-600 leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Available Grades */}
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Available Grades</h3>
              <div className="flex flex-wrap gap-3">
                {product.grades.map((grade) => (
                  <Badge key={grade} variant="secondary" className="text-sm px-3 py-1">
                    {grade}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Key Applications */}
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Key Applications</h3>
              <div className="space-y-3">
                {product.applications.map((app) => (
                  <div key={app} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-slate-700">{app}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                className="bg-[#022d47] hover:bg-[#033858] text-white px-8 py-3"
                onClick={() => window.open(companyInfo.whatsapp, '_blank')}
              >
                Get Instant Quote
              </Button>
            </div>
          </div>
        </div>

        <Separator className="my-16" />

        {/* Technical Specifications */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl lg:text-2xl text-slate-900">Technical Specifications</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Standards & Grades</h4>
                <div className="space-y-2">
                  {currentSpecs.standards.map((standard, index) => (
                    <div key={index} className="text-slate-600">• {standard}</div>
                  ))}
                </div>
              </div>

              {currentSpecs.sizes && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Available Sizes</h4>
                  <div className="space-y-2">
                    {currentSpecs.sizes.map((size, index) => (
                      <div key={index} className="text-slate-600">• {size}</div>
                    ))}
                  </div>
                </div>
              )}

              {currentSpecs.forms && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">{product.id === 5 || product.id === 6 || product.id === 7 ? 'Forms' : 'Manufacturing Forms'}</h4>
                  <div className="space-y-2">
                    {currentSpecs.forms.map((form, index) => (
                      <div key={index} className="text-slate-600">• {form}</div>
                    ))}
                  </div>
                </div>
              )}

              {currentSpecs.finishes && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Surface Finishes</h4>
                  <div className="space-y-2">
                    {currentSpecs.finishes.map((finish, index) => (
                      <div key={index} className="text-slate-600">• {finish}</div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Services & Support */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl lg:text-2xl text-slate-900">Our Services</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {[
                  { icon: Wrench, title: 'Custom Cutting', desc: 'Precision cutting to your exact specifications' },
                  { icon: Shield, title: 'Quality Testing', desc: 'Comprehensive material testing and certification' },
                  { icon: Clock, title: 'Quick Delivery', desc: 'Same-day dispatch for stock items' },
                  { icon: Star, title: 'Technical Support', desc: 'Expert consultation and application guidance' }
                ].map((service, index) => {
                  const IconComponent = service.icon;
                  return (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-[#022d47]/10 rounded-lg flex items-center justify-center mt-1">
                        <IconComponent className="h-5 w-5 text-[#022d47]" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-slate-900">{service.title}</h5>
                        <p className="text-slate-600 text-sm">{service.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Separator />

              <div className="bg-slate-50 p-4 rounded-lg">
                <h5 className="font-semibold text-slate-900 mb-2">Need Help?</h5>
                <p className="text-slate-600 text-sm mb-4">
                  Our technical team is ready to assist with material selection and specifications.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full hover:border-[#022d47] hover:text-[#022d47]"
                  onClick={() => window.location.href = 'mailto:vedaant@jpsmetal.in?subject=Technical Inquiry'}
                >
                  Contact Technical Team
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Related Products */}
        <div className="mt-16">
          <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Related Products
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {products
              .filter(p => p.id !== product.id)
              .slice(0, 3)
              .map((relatedProduct) => (
                <Card key={relatedProduct.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="aspect-w-16 aspect-h-9 overflow-hidden rounded-t-lg">
                    <img 
                      src={relatedProduct.image} 
                      alt={relatedProduct.name}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                    />
                  </div>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{relatedProduct.name}</CardTitle>
                    <CardDescription className="text-sm">
                      {relatedProduct.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      variant="outline" 
                      className="w-full hover:border-[#022d47] hover:text-[#022d47]"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};